import { makeConsent } from "@flow/common/consent_logic";
import { Consent, ConsentConfig, ConsentInput, ConsentLevel } from "@flow/common/types";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Typography,
  Radio,
  Box,
  Checkbox,
  Theme,
  SxProps,
  Tooltip,
  Alert,
} from "@mui/material";
import * as React from "react";
import { useState } from "react";
import { memberTheme } from "../member/memberTheme";

export interface ConsentInputComponentProps {
  inputName: string;
  updateConsent: (consent: Consent) => void;
  config: ConsentConfig;
  sx?: SxProps<Theme>;
}

export function ConsentInputComponent(props: ConsentInputComponentProps) {
  // actively-selected consent (ignoring defaults)
  // this is useful for the nag message when a member clicks 'no'
  const [givenConsentLevel, setGivenConsentLevel] = useState<ConsentLevel | undefined>(undefined);

  const setConsentLevel = (l: ConsentLevel) => {
    setGivenConsentLevel(l);
    props.updateConsent(makeConsent(l, props.config));
  };

  React.useEffect(() => {
    // Implicit consent has no input for us to attach an onchange to.
    // So need to do useEffect to set the consent level.
    if (props.config.input.type === "implicit") {
      setConsentLevel("implicit");
    }
  }, []);

  return (
    <Box
      key={props.inputName}
      sx={{
        ...props.sx,
      }}
    >
      {(() => {
        switch (props.config.input.type) {
          case "implicit":
            return implicitConsentField();
          // SW-200 - NB: Under the hood actually using the implicit consent type handler.
          case "onfieldpresent":
            return onfieldpresentConsentField();
          case "checkbox":
            return checkboxConsentField(props.config.input);
          case "radiobuttons":
            return radiobuttonsConsentField(props.config.input);
          default:
            return null;
        }
      })()}
    </Box>
  );

  function radiobuttonsConsentField(input: ConsentInput & { type: "radiobuttons" }): React.ReactNode {
    return (
      <>
        <RadioButtons
          name={`action-consent-${props.inputName}`}
          options={[
            {
              value: input.yes_consent_level,
              label: input.consent_yes_label,
            },
            { value: input.no_consent_level, label: input.consent_no_label },
          ]}
          error_msg_if_required={input.required_msg}
          onChange={(value) => setConsentLevel(value as ConsentLevel)}
          legend={props.config.short_text_override || props.config.consent_text_short_text}
        />
        {givenConsentLevel === input.no_consent_level && input.nag_msg && (
          <Alert role="alert" icon={false} severity="warning">
            <span dangerouslySetInnerHTML={{ __html: input.nag_msg }} />
          </Alert>
        )}
      </>
    );
  }

  function checkboxConsentField(
    input: { type: "checkbox" } & {
      consent_label: string;
      consent_help: string;
      required: boolean;
      mapping: [ConsentLevel, ConsentLevel];
    }
  ): React.ReactNode {
    return (
      <>
        <Typography
          variant="subtitle2"
          display="inline"
          dangerouslySetInnerHTML={{
            __html: props.config.short_text_override || props.config.consent_text_short_text,
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              required={input.required}
              onChange={(e) => setConsentLevel(e.target.checked ? input.mapping[1] : input.mapping[0])}
            />
          }
          label={
            <Tooltip title={input.consent_help} arrow>
              <Typography variant="body2">{input.consent_label}</Typography>
            </Tooltip>
          }
        />
      </>
    );
  }

  function implicitConsentField(): React.ReactNode {
    return (
      <>
        <Typography
          sx={{ "& a": { color: memberTheme.palette.blue.main } }}
          variant="caption"
          dangerouslySetInnerHTML={{
            __html: props.config.short_text_override || props.config.consent_text_short_text,
          }}
        />
      </>
    );
  }

  // SW-200 - NB: We currently use the implicit consent type handler for the onfieldpresent type.
  //          This is currently only for 'Email Is Opt-In' so we only need to show the consent
  //          text and do not require any additional HTML inputs beyond the email input field.
  function onfieldpresentConsentField(): React.ReactNode {
    return implicitConsentField();
  }
}

interface Option {
  label: string;
  value: string;
}

interface RadioButtonsProps {
  name: string;
  options: Option[];
  error_msg_if_required: undefined | string;
  onChange: (value: string) => void;
  className?: string;
  legend: string;
}

function RadioButtons(props: RadioButtonsProps) {
  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove custom validity message from all radio inputs
    const parentElement = e.currentTarget.parentElement;
    if (parentElement) {
      const inputs = parentElement.querySelectorAll("input");
      inputs.forEach((input) => input.setCustomValidity(""));
    }
    props.onChange(e.target.value);
  };

  return (
    <FormControl required={!!props.error_msg_if_required} component="fieldset">
      <FormLabel>
        {/* TODO: currently using a hack to prevent arbitrary HTML input */}
        <Typography variant="subtitle2" display="inline">
          {removeHtmlTags(props.legend)}
        </Typography>
      </FormLabel>

      <RadioGroup name={props.name} onChange={handleRadioChange}>
        {props.options.map((option, i) => (
          <FormControlLabel
            key={i}
            label={
              <Typography display={"inline-flex"} variant="body2">
                {option.label}
              </Typography>
            }
            value={option.value}
            sx={{
              "& .MuiFormControlLabel-asterisk": {
                display: "none",
              },
            }}
            control={<Radio id={`${props.name}-${i}`} required={!!props.error_msg_if_required} sx={{ paddingY: 0 }} />}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

function removeHtmlTags(inputString: string) {
  return inputString.replace(/<[^>]*>/g, "");
}
